import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  appKeySchema,
  bodyErrors,
  errorSchema,
  type ErrorCode,
  guardErrors,
  idParams,
  targetErrors,
  MIN_PASSWORD_LENGTH,
} from '../common';
import { adminSummarySchema, userWithAccessSchema } from '../entities';

const c = initContract();

export const adminContract = c.router(
  {
    overview: {
      method: 'GET',
      path: '/overview',
      responses: { 200: adminSummarySchema, ...guardErrors },
      summary: 'User/app counts, container disk and per-app database usage',
    },

    listUsers: {
      method: 'GET',
      path: '/users',
      responses: { 200: z.array(userWithAccessSchema), ...guardErrors },
      summary: 'Every user with their app grants',
    },

    createUser: {
      method: 'POST',
      path: '/users',
      body: z
        .object({
          login: z
            .string()
            .transform((s) => s.trim())
            .refine((s) => s.length >= 1, {
              message: 'login_required' satisfies ErrorCode,
            }),
          password: z.string().min(MIN_PASSWORD_LENGTH, {
            message: 'password_too_short' satisfies ErrorCode,
          }),
          passwordConfirm: z.string(),
          /** Granted in the same transaction the user is created in. */
          apps: z.array(appKeySchema).default([]),
        })
        .refine((b) => b.password === b.passwordConfirm, {
          message: 'passwords_mismatch' satisfies ErrorCode,
          path: ['passwordConfirm'],
        }),
      responses: { 201: userWithAccessSchema, ...bodyErrors, 409: errorSchema },
      summary: 'Create a user with their app grants (login is case-sensitive)',
    },

    /**
     * Replaces the whole grant set for a user — apps absent from the array are
     * revoked, which is what the checkbox form means by submitting.
     */
    setAccess: {
      method: 'POST',
      path: '/users/:id/access',
      pathParams: idParams,
      body: z.object({ apps: z.array(appKeySchema) }),
      responses: { 200: userWithAccessSchema, ...targetErrors },
      summary: 'Set a user’s app grants to exactly this list',
    },

    deleteUser: {
      method: 'DELETE',
      path: '/users/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
      summary: 'Delete a user; cascades tokens and grants. Self-delete is 403',
    },
  },
  { pathPrefix: '/admin' },
);
